// FILE: components/shared/UnlockButton.tsx
// Button mở khóa với hiệu ứng kim cương bay lên (Mario coin)
// Trigger effect qua prop `flying` do parent control — tránh bay sai timing (vd khi số dư không đủ)
"use client";

import { useState } from "react";
import Button from "./Button";

interface Props {
  onClick: () => void | Promise<void>;
  disabled?: boolean;
  children?: React.ReactNode;
  /** Tone & size pass xuống Button. Mặc định orange/md cho CTA mở khóa. */
  tone?: "orange" | "amber" | "teal";
  size?: "sm" | "md" | "lg";
  className?: string;
  /** Nếu true, đợi animation 700ms xong rồi mới gọi onClick (dùng khi onClick đóng modal) */
  awaitAnimation?: boolean;
}

let counter = 0; // unique key cho mỗi diamond bay (tránh conflict khi click nhanh)

interface Diamond { id: number; }

export default function UnlockButton({
  onClick,
  disabled,
  children = "Mở khóa",
  tone = "orange",
  size = "md",
  className = "",
  awaitAnimation = false,
}: Props) {
  const [diamonds, setDiamonds] = useState<Diamond[]>([]);

  const handleClick = async () => {
    // Spawn diamond NGAY khi click → user thấy phản hồi tức thì
    const id = ++counter;
    setDiamonds((prev) => [...prev, { id }]);
    setTimeout(() => {
      setDiamonds((prev) => prev.filter((d) => d.id !== id));
    }, 700);

    // awaitAnimation=true: đợi animation xong rồi mới gọi onClick (vd khi onClick đóng modal)
    if (awaitAnimation) {
      await new Promise((r) => setTimeout(r, 600));
    }
    await onClick();
  };

  return (
    <span className={`relative inline-block ${className}`}>
      <Button
        variant="arcade"
        tone={tone}
        size={size}
        onClick={handleClick}
        disabled={disabled}
        className={className.includes("flex-1") || className.includes("w-full") ? "w-full !h-auto py-3 !rounded-xl !text-[13px]" : ""}
      >
        {children}
      </Button>

      {/* Diamonds bay lên — pure CSS keyframe, không cần Framer Motion */}
      {diamonds.map((d) => (
        <span
          key={d.id}
          className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[20px] animate-[diamond-fly_700ms_ease-out_forwards]"
          aria-hidden
        >
          💎
        </span>
      ))}

      {/* Inline keyframe — tránh phải sửa global tailwind config */}
      <style jsx>{`
        @keyframes diamond-fly {
          0%   { transform: translate(-50%, -50%) scale(0.6); opacity: 0; }
          20%  { transform: translate(-50%, -50%) scale(1.2); opacity: 1; }
          100% { transform: translate(-50%, -180%) scale(0.8); opacity: 0; }
        }
      `}</style>
    </span>
  );
}
